import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import {
  Controller,
  type ControllerProps,
  type FieldError,
  type FieldPath,
  type FieldValues,
  FormProvider,
  useFormContext,
} from "react-hook-form";
import { cn } from "@repo/cn";
import { Button, type ButtonProps } from "@/components/ui/button/button";

import type { JSX } from "react";

export const FormRoot = FormProvider;

interface FormProps extends React.ComponentPropsWithoutRef<"form"> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- react hook forms SubmitHandler not generic
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  handleSubmit?: (data: any) => Promise<void> | void;
}

export const FormHandlerSubmit = ({
  handleSubmit,
  className,
  ...props
}: FormProps): JSX.Element => {
  const form = useFormContext();

  return (
    <form
      className={cn("max-w-2xl mx-auto flex flex-col gap-6", className)}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- react hook forms
      onSubmit={handleSubmit ? form.handleSubmit(handleSubmit) : undefined}
      {...props}
    />
  );
};

interface FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
}

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
);

export const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>({
  ...props
}: ControllerProps<TFieldValues, TName, TTransformedValues>): JSX.Element => {
  return (
    <FormFieldContext value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext>
  );
};

const useFormField = (): {
  invalid: boolean;
  isDirty: boolean;
  isTouched: boolean;
  isValidating: boolean;
  error?: FieldError | undefined;
  id: string;
  name: string;
  formItemId: string;
  formMessageId: string;
} => {
  const fieldContext = React.use(FormFieldContext);
  const itemContext = React.use(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- not always
  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

interface FormItemContextValue {
  id: string;
}

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
);

export const FormItem = ({
  ref,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const id = React.useId();

  return (
    <FormItemContext value={{ id }}>
      <div ref={ref} className={cn("space-y-2", className)} {...props} />
    </FormItemContext>
  );
};
FormItem.displayName = "FormItem";

export const FormControl = ({
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof Slot> & {
  ref?: React.Ref<React.ComponentRef<typeof Slot>>;
}) => {
  const { error, formItemId, formMessageId } = useFormField();

  return (
    <>
      <Slot
        ref={ref}
        id={formItemId}
        aria-describedby={error ? formMessageId : ""}
        aria-invalid={Boolean(error)}
        {...props}
      />
      <FormMessage />
    </>
  );
};
FormControl.displayName = "FormControl";

const FormMessage = ({
  ref,
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement> & {
  ref?: React.Ref<HTMLParagraphElement>;
}) => {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error.message) : children;

  if (!body) {
    return null;
  }

  return (
    <FormErrorMessage id={formMessageId} ref={ref} className={className} {...props}>
      {body}
    </FormErrorMessage>
  );
};
FormMessage.displayName = "FormMessage";

export const FormErrorMessage = ({
  ref,
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement> & {
  ref?: React.Ref<HTMLParagraphElement>;
}) => {
  if (!children) {
    return null;
  }

  return (
    <p
      ref={ref}
      className={cn("text-sm font-medium text-danger-400", className)}
      {...props}
    >
      {children}
    </p>
  );
};
FormErrorMessage.displayName = "FormErrorMessage";

type FormSubmitButtonProps = ButtonProps & {
  as?: React.ElementType;
};

export const FormSubmitButton = ({
  className,
  as: Component = Button,
  ...props
}: FormSubmitButtonProps): JSX.Element => {
  const {
    formState: { isSubmitting },
  } = useFormContext();

  return (
    <div className={cn("space-y-2", className)}>
      <Component
        type="submit"
        fullWidth
        loading={isSubmitting}
        disabled={isSubmitting}
        {...props}
      />
    </div>
  );
};
FormSubmitButton.displayName = "FormSubmitButton";
